import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, useLoaderData } from "@tanstack/react-router"
import { Trans, useTranslation } from "react-i18next"

import { softDeletePhoto, useSessionDetail, useStepWithMedia } from "@/services/dexie"

import { ClientRedirect } from "../../../../components/ClientRedirect"
import { ImageLayout } from "../../../../components/layouts/ImageLayout"
import { SpinnerLayout } from "../../../../components/layouts/SpinnerLayout"
import { useUpsertMultiplePhotosWithLocation } from "../../../../helpers/hooks/useStore"
import { useScreenTypeIntroText } from "../../../../locales/photoType"
import { LINKS } from "../../../../routing/links"
import { useFinishStepAndNavigate, useStepsRouting } from "../../../../routing/useStepsRouting"
import { ExtraEquipmentPhotos } from "./-ExtraEquipmentPhotos"

export const Route = createFileRoute("/$sessionId/_session/extra-equipment/select")({
  component: ExtraEquipment,
})

function ExtraEquipment() {
  const { t } = useTranslation()
  const { sessionId } = Route.useParams()
  const { enableUpload } = useLoaderData({ from: "/$sessionId/_session/extra-equipment" })

  const finishAndNavigate = useFinishStepAndNavigate(sessionId)
  const { firstUnfinishedStepLink, isLoading: stepsLoading } = useStepsRouting(sessionId)

  const { data: detail } = useSessionDetail(sessionId)
  const { data: step, isLoading: stepLoading } = useStepWithMedia(sessionId, "ExtraEquipment")
  const selectPhoto = useUpsertMultiplePhotosWithLocation("ExtraEquipment", enableUpload, sessionId)

  const vehicleType = detail?.vehicleType ?? "PersonalVehicle"
  const { text, primaryActionTitle, title } = useScreenTypeIntroText({ vehicleType, stepType: "ExtraEquipment" })

  /* Loading state */
  if (stepLoading || stepsLoading) return <SpinnerLayout />

  /* No step data available */
  if (!step) {
    return <ClientRedirect to={firstUnfinishedStepLink ?? LINKS.finalCheck(sessionId)} />
  }

  /* At least one photo taken */
  if (step.photosCount > 0) {
    return (
      <ExtraEquipmentPhotos
        photos={step.photos}
        isMinMediaSatisfied={step.isMinMediaSatisfied}
        onAddMore={selectPhoto}
        onDelete={softDeletePhoto}
        onDone={() => finishAndNavigate("ExtraEquipment")}
      />
    )
  }

  /* No photo yet */
  return (
    <ImageLayout
      dataTestId="extra-equipment-select"
      iconName="ExtraEquipment"
      content={
        <>
          <Heading as="h2" variant="h1">
            {title}
          </Heading>

          <div className="h-6 w-6 shrink-0" />

          <Text>
            <Trans
              defaults={text}
              components={{
                br: <br />,
                bold: <strong className="font-bold" />,
                span: <span className="text-ic-primary font-bold" />,
              }}
            />
          </Text>
        </>
      }
      footer={
        <>
          <div className="h-5 w-5 shrink-0" />

          <div className="flex justify-center">
            <Button data-testid="extra-equipment-take-photos-btn" onClick={selectPhoto}>
              {primaryActionTitle}
            </Button>
          </div>

          {step.canSkip && (
            <>
              <div className="h-2 w-2 shrink-0" />

              <div className="flex justify-center">
                <Button
                  data-testid="extra-equipment-no-equipment-btn"
                  variant="secondary"
                  disabled={!step.isMinMediaSatisfied}
                  onClick={() => finishAndNavigate("ExtraEquipment")}
                >
                  {t("extra.equipment.intro.secondaryActionTitle")}
                </Button>
              </div>
            </>
          )}

          <div className="h-11 w-11 shrink-0" />
        </>
      }
    />
  )
}
